import type { ImageSrcOverrides } from "@/shared/messageImageSrc.ts";
import type { MessageClientState } from "@/types/messages.ts";
import { createContext, useContext } from "react";

export type MarkdownContextType = {
  /**
   * Drives the character-reveal enter animation. Callers may hold this `true`
   * for as long as the block is treated as freshly streamed, which can outlast
   * the text actually growing — so it is not a safe gate for affordances that
   * need complete text. Use {@link MarkdownContextType.isTextStillArriving}.
   */
  isStreaming?: boolean;
  /**
   * Whether the rendered text is still growing. Gates affordances that would
   * otherwise act on a partial value, such as the fenced-code copy button.
   */
  isTextStillArriving?: boolean;
  /**
   * The message this markdown belongs to, when it is one. Present only where
   * the markdown is a persisted message rather than loose text (an artifact
   * body, a debug blob), which is what lets a one-shot `action:` link record
   * that it ran. An action with no message to record against does not fire —
   * there would be nothing to stop it firing again on the next render.
   */
  message?: { id: number; clientState: MessageClientState };
  /**
   * The text came from someone other than the user or okthx — a saved note or
   * page. Its links only navigate: one that reads like a task answer opens the
   * task rather than answering it.
   */
  untrusted?: boolean;
  /**
   * A shared reader's paths for the file images this markdown shows. Their
   * session cannot reach the owner's image routes, so an image only loads
   * through one of these.
   */
  imageSrcs?: ImageSrcOverrides;
};
export const MarkdownContext = createContext<MarkdownContextType | undefined>(undefined);

export const useMarkdownContext = () => {
  const context = useContext(MarkdownContext);
  if (context === undefined) {
    throw new Error("useMarkdownContext must be used within a MarkdownContextProvider");
  }
  return context;
};
